/**
 * La préférence d'apparence de l'espace parent — Automatique, Clair ou Sombre —, gardée sur
 * l'appareil.
 *
 * Automatique est le défaut, et c'est le comportement d'avant le réglage : l'espace parent suit
 * l'apparence du système. Clair et Sombre la forcent, pour l'espace parent seulement. Ce que la
 * valeur change est écrit ailleurs : `parentThemeFor` pour le thème du design system,
 * `parentAppearance` pour l'apparence native d'iOS.
 *
 * Un magasin de module plutôt qu'une tranche Redux : c'est un réglage de l'APPAREIL, pas une
 * donnée du compte — il survit à un changement de compte et ne part jamais au backend —, et il a
 * deux lecteurs qui ne se voient pas (la coquille qui l'applique, la feuille « Changer de profil »
 * qui le change). `useSyncExternalStore` les tient d'accord sans provider.
 *
 * La valeur se lit une fois dans le stockage, au premier lecteur, puis s'y écrit à chaque choix. Une valeur inconnue ou une lecture en échec vaut
 * Automatique : c'est le seul défaut qui ne change rien pour personne.
 */
import { useEffect, useSyncExternalStore } from "react"

import { getFromStorage, setToStorage } from "../../../../data/utility/asyncStorageUtils"
import {
    createDevicePreferenceStore,
    type DevicePreferenceStorage,
    type DevicePreferenceStore,
} from "./devicePreferenceStore"

export type ParentAppearancePreference = "auto" | "light" | "dark"

/** Clé explicite : c'est elle qu'on retrouve en inspectant le stockage de l'appareil. */
export const PARENT_APPEARANCE_STORAGE_KEY = "masteur.parentSpace.appearance"

/**
 * Ce que le stockage rend, ramené aux trois valeurs connues — tout le reste vaut le défaut de
 * l'espace (`auto` chez le parent ; l'espace élève passe le sien).
 */
export function toParentAppearancePreference(
    raw: unknown,
    fallback: ParentAppearancePreference = "auto"
): ParentAppearancePreference {
    return raw === "auto" || raw === "light" || raw === "dark" ? raw : fallback
}

/**
 * Ce qui distingue un espace d'un autre : la clé où son choix est gardé, et ce qu'il vaut tant que
 * rien n'est gardé. Automatique par défaut, pour le parent comme pour l'élève
 * (`studentAppearancePreference`).
 */
export type AppearanceStoreOptions = {
    storageKey?: string
    fallback?: ParentAppearancePreference
}

/** Le magasin, séparé de son stockage pour se tester sans module simulé. */
export function createParentAppearanceStore(
    storage: DevicePreferenceStorage,
    options: AppearanceStoreOptions = {}
): DevicePreferenceStore<ParentAppearancePreference> {
    const { storageKey = PARENT_APPEARANCE_STORAGE_KEY, fallback = "auto" } = options
    return createDevicePreferenceStore(storage, {
        storageKey,
        fallback,
        parse: (raw) => toParentAppearancePreference(raw, fallback),
    })
}

const store = createParentAppearanceStore({ read: getFromStorage, write: setToStorage })

/** Le geste de la feuille « Changer de profil » : le choix s'applique tout de suite, partout. */
export const setParentAppearancePreference = store.set

/** La préférence courante, relue à chaque changement. */
export function useParentAppearancePreference(): ParentAppearancePreference {
    const preference = useSyncExternalStore(store.subscribe, store.get, store.get)

    useEffect(() => {
        void store.hydrate()
    }, [])

    return preference
}
